/**
 * Owner: Masa Bayu
 * File: PrivateAuthorHero.tsx
 * Date:Time: 2026-10-23 08:40
 * Description: Hero dashboard PRIVAT §467-C — nama + avatar inisial + role +
 *   e-mel + tahun = nilai asal `GET /v1/publisher/dashboard`. Kad statistik:
 *   terbitan NYATA; pengikut dan rating dipaparkan "tiada data" kerana hub
 *   belum ada sumber. Dibuang: potret stok, lencana verified, kutipan orang
 *   lain, geografi tetap dan bar "matlamat bulan" karangan.
 *   §421 — butang Studio (laluan dalaman /author/studio) kekal sebaris Edit Profil.
 *   #383 — chip e-mel/tahun dibalut `.qxp__hero-chip-text` (elipsis + `title`)
 *     supaya e-mel panjang tidak melimpah pada jalur 768–1160.
 */

import type { ReactElement, ReactNode } from 'react';
import { STUDIO_ENTRY } from '../author-production-identity';
import type { PrivateAuthorLabels } from './private-author-labels';
import type { AuthorDashboardModel } from './private-author-view';

const HERO_BANNER = '/author/banner.png';

function Stroke({
  children,
  size = 16,
}: {
  readonly children: ReactNode;
  readonly size?: number;
}): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export interface PrivateAuthorHeroProps {
  readonly onNavigate: (path: string) => void;
  readonly la: PrivateAuthorLabels;
  readonly model: AuthorDashboardModel;
}

export function PrivateAuthorHero({ onNavigate, la, model }: PrivateAuthorHeroProps): ReactElement {
  const stats = [
    { value: String(model.booksPublished), label: la.statBooks },
    { value: model.noData, label: la.statFollowers },
    { value: model.noData, label: la.statRating },
  ];
  return (
    <section className="qxp__hero" aria-label={la.role}>
      <img className="qxp__hero-banner" src={HERO_BANNER} alt="" />
      <div className="qxp__hero-id">
        <div className="qxp__hero-portrait" aria-hidden="true">
          <span className="qxp__hero-avatar">{model.initials}</span>
        </div>
        <div className="qxp__hero-meta">
          <h1 className="qxp__hero-name">{model.name}</h1>
          <p className="qxp__hero-role">{la.role}</p>
          <div className="qxp__hero-chips">
            {model.email === null ? null : (
              <span className="qxp__hero-chip">
                <Stroke size={14}>
                  <rect x="3" y="5" width="18" height="14" rx="2" />
                  <path d="M3 7l9 6 9-6" />
                </Stroke>
                <span className="qxp__hero-chip-text" title={model.email}>
                  {model.email}
                </span>
              </span>
            )}
            {model.sinceYear === null ? null : (
              <span className="qxp__hero-chip">
                <Stroke size={14}>
                  <rect x="3" y="5" width="18" height="16" rx="2" />
                  <path d="M8 3v4M16 3v4M3 10h18" />
                </Stroke>
                <span className="qxp__hero-chip-text">
                  {la.since} {model.sinceYear}
                </span>
              </span>
            )}
          </div>
        </div>
        <div className="qxp__hero-side">
          <div className="qxp__hero-btns">
            <button
              type="button"
              className="qxp__edit-btn qx-motion-press"
              onClick={() => {
                onNavigate('/author/profile');
              }}
            >
              <Stroke size={15}>
                <path d="M4 20l1-4L16 5l3 3L8 19z" />
                <path d="M14 7l3 3" />
              </Stroke>
              {la.editProfile}
            </button>
            <button
              type="button"
              className="qxp__edit-btn qxp__editor-btn qx-motion-press"
              onClick={() => {
                onNavigate(STUDIO_ENTRY);
              }}
              aria-label={la.editorButtonAria}
              data-qxeditor-entry="author-dashboard"
            >
              <Stroke size={15}>
                <path d="M11 4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2v-5" />
                <path d="M18.5 2.5a2.1 2.1 0 013 3L12 15l-3.5 1 1-3.5z" />
              </Stroke>
              {la.editorButton}
            </button>
          </div>
        </div>
      </div>
      <div className="qxp__hero-card">
        <div className="qxp__hero-stats" role="group" aria-label={la.statBooks}>
          {stats.map((stat, i) => (
            <div className="qxp__hero-stat" key={stat.label}>
              {i > 0 ? <span className="qxp__hero-divider" aria-hidden="true" /> : null}
              <div>
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="qxp__hero-month">
          <h2>{la.monthSalesLabel}</h2>
          <strong>{model.monthRevenueText}</strong>
        </div>
        <p className="qxp__hero-tagline">
          <Stroke size={15}>
            <path d="M20 4C10 6 6 12 4 20c8-2 14-6 16-16z" />
            <path d="M4 20L12 12" />
          </Stroke>
          {la.tagline}
        </p>
      </div>
    </section>
  );
}
